import { useEffect, useState } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";

/**
 * Minimal `beforeinstallprompt` typing (not in the standard lib DOM types).
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  // iOS Safari
  (window.navigator as unknown as { standalone?: boolean }).standalone === true;

/**
 * App-wide PWA UI: an "update available" toast (from the service worker) and a
 * custom "Install app" button (from the browser's install prompt). Self-contained
 * — renders its own fixed-position UI and does not depend on any toast provider.
 */
export function PWABadge() {
  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(swUrl, registration) {
      // Poll for a new service worker every hour so long-lived tabs pick up deploys.
      if (registration) {
        setInterval(
          () => {
            registration.update().catch(() => {});
          },
          60 * 60 * 1000
        );
      }
    },
  });

  const [installEvent, setInstallEvent] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setInstallEvent(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setInstallEvent(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const closeUpdateToast = () => {
    setOfflineReady(false);
    setNeedRefresh(false);
  };

  const handleInstall = async () => {
    if (!installEvent) {
      return;
    }
    await installEvent.prompt();
    const { outcome } = await installEvent.userChoice;
    if (outcome === "accepted") {
      setInstalled(true);
    }
    setInstallEvent(null);
  };

  const showInstall = !installed && installEvent !== null;
  const showUpdate = offlineReady || needRefresh;

  if (!showInstall && !showUpdate) {
    return null;
  }

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[9999] flex flex-col items-center gap-3 p-4 sm:items-end">
      {showUpdate && (
        <div className="pointer-events-auto w-full max-w-sm rounded-lg border border-black/5 bg-white p-4 shadow-lg">
          <p className="text-sm font-medium text-gray-900">
            {needRefresh
              ? "A new version is available."
              : "App ready to work offline."}
          </p>
          <div className="mt-3 flex justify-end gap-2">
            {needRefresh && (
              <button
                type="button"
                onClick={() => updateServiceWorker(true)}
                className="rounded-md px-3 py-1.5 text-sm font-medium text-white"
                style={{ backgroundColor: "#404bb5" }}
              >
                Reload
              </button>
            )}
            <button
              type="button"
              onClick={closeUpdateToast}
              className="rounded-md px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100"
            >
              {needRefresh ? "Later" : "Dismiss"}
            </button>
          </div>
        </div>
      )}

      {showInstall && (
        <button
          type="button"
          onClick={handleInstall}
          className="pointer-events-auto inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition-transform hover:scale-105"
          style={{ backgroundColor: "#404bb5" }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 3v12" />
            <path d="m7 10 5 5 5-5" />
            <path d="M5 21h14" />
          </svg>
          Install app
        </button>
      )}
    </div>
  );
}
